import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TaskEditDialog from "@/components/TaskEditDialog";
import PriorityScoreBar from "@/components/PriorityScoreBar";
import TrackerPanel from "@/components/TrackerPanel";
import {
  Card, SectionTitle, Pill, ProjectBadge, StatusBadge, DueBadge, DEPT_LABELS, aed, Loader, EmptyState,
} from "@/components/common";
import {
  FolderKanban, Plus, X, ChevronDown, ChevronRight, Trash2, RotateCcw, Flame, Pencil, Lock, Inbox, Upload, FileSpreadsheet,
} from "lucide-react";

const SECTORS = ["hospitality", "residential", "commercial", "retail"];

export default function Projects() {
  const { user, canEditDept, canManageProjects, isAdmin } = useAuth();
  const isCommercial = user?.role === "commercial";
  const canManage = canManageProjects;
  const [projects, setProjects] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [trackerData, setTrackerData] = useState({});
  const [openTracker, setOpenTracker] = useState({});
  const [trackerImport, setTrackerImport] = useState(null);

  const loadTracker = async (pid) => {
    try {
      const { data } = await api.get(`/tracker/${pid}`);
      setTrackerData((s) => ({ ...s, [pid]: data.by_dept || {} }));
    } catch { /* ignore */ }
  };
  const toggleExpand = (pid) => {
    const opening = expanded !== pid;
    setExpanded(opening ? pid : null);
    if (opening) loadTracker(pid);
  };
  const [form, setForm] = useState(null); // {mode, project}
  const [editingTask, setEditingTask] = useState(null);
  const [openMaterials, setOpenMaterials] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, t] = await Promise.all([api.get("/projects"), api.get("/tasks")]);
    setProjects(p.data);
    setTasks(t.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetDemo = async () => {
    if (!window.confirm("Remove all demo/sample projects and their tasks? Your real projects are not affected. This cannot be undone.")) return;
    setBusy(true);
    try { await api.post("/admin/reset-demo"); await load(); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project and all its pipeline tasks?")) return;
    await api.delete(`/projects/${id}`); await load();
  };

  if (!projects) return <Loader label="Loading projects" />;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight" data-testid="page-title">Projects</h1>
          <p className="text-muted-foreground text-sm mt-1">{projects.length} project(s) · pipeline auto-generated & back-scheduled.</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {isCommercial && (
              <button onClick={resetDemo} disabled={busy} data-testid="reset-demo-btn"
                className="flex items-center gap-2 px-3.5 py-2 rounded-md border border-border text-sm text-muted-foreground hover:text-critical hover:border-critical/40">
                <RotateCcw size={15} /> Clear demo data
              </button>
            )}
            <button onClick={() => setForm({ mode: "create" })} data-testid="new-project-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-gold text-primary-foreground font-semibold text-sm hover:opacity-90">
              <Plus size={16} /> New project
            </button>
          </div>
        )}
      </div>

      {projects.length === 0 ? (
        <Card className="p-2"><EmptyState icon={FolderKanban} title="No projects yet" subtitle={isCommercial ? "Create your first project to auto-generate its parallel-track pipeline." : "Ask Commercial to create a project."} /></Card>
      ) : (
        <div className="space-y-3">
          {projects.map((p) => {
            const pTasks = tasks.filter((t) => t.project_id === p.id).sort((a, b) => a.days_left - b.days_left);
            const overdue = pTasks.filter((t) => t.overdue).length;
            const materialItems = pTasks.find((t) => t.reference_items?.length)?.reference_items || [];
            const isOpen = expanded === p.id;
            const awaiting = p.status === "awaiting_commercial";
            return (
              <Card key={p.id} data-testid={`project-${p.job_code}`} className={awaiting ? "border-gold/40" : ""}>
                <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-4 cursor-pointer" onClick={() => toggleExpand(p.id)}>
                  <button className="text-muted-foreground shrink-0">
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{p.name}</span>
                      {awaiting && <Pill tone="gold" data-testid="badge-awaiting"><Inbox size={11} /> Awaiting commercial</Pill>}
                      {p.is_demo && <Pill tone="muted">Demo</Pill>}
                      {p.source === "polaris-estimation" && <Pill tone="info">From estimator</Pill>}
                      {p.compressed && <Pill tone="gold"><Flame size={11} /> Compressed</Pill>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.job_code} · {p.client_name}{p.sector ? ` · ${p.sector}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-right">
                      <div className="font-mono-num">{aed(p.contract_value_aed)}</div>
                      <div className="text-xs text-muted-foreground">{p.completion_date ? `by ${p.completion_date}` : "no completion date"}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={`w-1.5 h-4 rounded-sm ${i < (p.client_importance || 0) ? "bg-gold" : "bg-muted"}`} />
                      ))}
                    </div>
                    {p.ld_applicable && <Pill tone="warning">LD</Pill>}
                    {overdue > 0 && <Pill tone="critical">{overdue} overdue</Pill>}
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {awaiting ? (
                        <button onClick={() => setForm({ mode: "edit", project: p })} data-testid={`activate-project-${p.job_code}`}
                          className="px-3 py-2 rounded-md bg-gold text-primary-foreground text-xs font-semibold hover:opacity-90">Activate</button>
                      ) : (
                        <button onClick={() => setForm({ mode: "edit", project: p })} data-testid={`edit-project-${p.job_code}`}
                          className="p-2 rounded-md border border-border text-muted-foreground hover:text-gold hover:border-gold/40"><Pencil size={14} /></button>
                      )}
                      <button onClick={() => del(p.id)} data-testid={`delete-project-${p.job_code}`}
                        className="p-2 rounded-md border border-border text-muted-foreground hover:text-critical hover:border-critical/40"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t border-border">
                      {awaiting && (
                        <div className="px-4 py-3 bg-gold/5 border-b border-border text-sm text-muted-foreground flex items-center gap-2">
                          <Inbox size={15} className="text-gold" />
                          Imported from estimation. Set completion date, LD and client importance to run backward scheduling and activate.
                        </div>
                      )}
                      <div className="p-4 space-y-2">
                        {pTasks.map((t) => {
                          const editable = canEditDept(t.dept);
                          const refCount = t.reference_items?.length || 0;
                          const isMaterialStage = t.stage_type === "material";
                          const showList = isMaterialStage && !!openMaterials[p.id];
                          const trackerDept = t.stage_type === "drawing_approval" ? "design" : t.stage_type === "material_approval" ? "material" : null;
                          const trackerGroups = trackerDept ? (trackerData[p.id]?.[trackerDept] || []) : [];
                          const canImport = trackerDept && (isAdmin || canEditDept(trackerDept));
                          const showTracker = trackerDept && !!openTracker[t.id];
                          return (
                            <React.Fragment key={t.id}>
                            <div className="flex flex-col md:flex-row md:items-center gap-3 py-2 border-b border-border/50 last:border-0" data-testid={`pipeline-task-${t.stage_type}`}>
                              <div className="md:w-[26%]">
                                <div className="text-sm font-medium">{t.stage_label}</div>
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                  <Pill tone="muted">{DEPT_LABELS[t.dept]}</Pill>
                                  {t.stage_type === "procurement" && (
                                    <Pill tone="gold" data-testid="proc-type-pill">
                                      {t.procurement_type === "sub_contract" ? "Sub-Contract" : "Material PO"}
                                    </Pill>
                                  )}
                                  {t.stage_type === "procurement" && refCount > 0 && (
                                    <Pill tone="info" data-testid="material-count">{refCount} materials</Pill>
                                  )}
                                  {isMaterialStage && materialItems.length > 0 && (
                                    <button
                                      type="button"
                                      data-testid="toggle-material-list"
                                      onClick={() => setOpenMaterials((s) => ({ ...s, [p.id]: !s[p.id] }))}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border border-info/40 text-info hover:bg-info/10 transition-colors"
                                    >
                                      {showList ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                      {materialItems.length} material items
                                    </button>
                                  )}
                                  {trackerDept && t.tracker_driven && (
                                    <button type="button" data-testid={`toggle-tracker-${trackerDept}`}
                                      onClick={() => setOpenTracker((s) => ({ ...s, [t.id]: !s[t.id] }))}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border border-gold/40 text-gold hover:bg-gold/10 transition-colors">
                                      {showTracker ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                      Tracker {t.tracker_approved}/{t.tracker_total}
                                    </button>
                                  )}
                                  {canImport && (
                                    <button type="button" data-testid={`import-tracker-${trackerDept}`}
                                      onClick={() => setTrackerImport({ projectId: p.id, projectName: p.name, dept: trackerDept })}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border border-border text-muted-foreground hover:bg-muted transition-colors">
                                      <Upload size={11} /> Import log
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 md:w-[22%]">
                                <StatusBadge status={t.status} complete={t.is_complete} />
                                {t.tracker_driven && <Pill tone="gold">Tracker-driven</Pill>}
                                {t.blocked && <Pill tone="warning">Blocked</Pill>}
                              </div>
                              {t.scheduling_pending ? (
                                <div className="flex-1 text-xs text-muted-foreground italic">Not scheduled yet · {t.planned_hours}h planned</div>
                              ) : (
                                <>
                                  <div className="md:w-[16%]"><DueBadge daysLeft={t.days_left} overdue={t.overdue} /></div>
                                  <div className="flex-1 min-w-[140px]"><PriorityScoreBar task={t} height={6} /></div>
                                </>
                              )}
                              <button onClick={() => setEditingTask(t)} data-testid={`open-task-${t.id}`}
                                className={`p-2 rounded-md border ${editable ? "border-gold/30 text-gold hover:bg-gold/10" : "border-border text-muted-foreground hover:bg-muted"}`}>
                                {editable ? <Pencil size={14} /> : <Lock size={14} />}
                              </button>
                            </div>
                            {showList && <MaterialList items={materialItems} />}
                            {showTracker && (
                              <TrackerPanel
                                groups={trackerGroups}
                                projectId={p.id}
                                dept={trackerDept}
                                canEdit={canImport}
                                onChange={async () => { await loadTracker(p.id); await load(); }}
                              />
                            )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            );
          })}
        </div>
      )}

      {form && <ProjectForm form={form} onClose={() => setForm(null)} onSaved={load} />}
      {editingTask && <TaskEditDialog task={editingTask} onClose={() => setEditingTask(null)} onSaved={load} />}
      {trackerImport && (
        <TrackerImportDialog
          info={trackerImport}
          onClose={() => setTrackerImport(null)}
          onCommitted={async () => { await load(); await loadTracker(trackerImport.projectId); setTrackerImport(null); }}
        />
      )}
    </div>
  );
}

function MaterialList({ items }) {
  return (
    <div className="mb-2 rounded-md border border-info/25 bg-info/5 overflow-hidden" data-testid="material-list">
      <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-info/90 border-b border-info/20 flex items-center gap-1.5">
        <Inbox size={12} /> Material list (from estimator · read-only)
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground/80 text-left">
              <th className="px-3 py-1.5 font-medium">#</th>
              <th className="px-3 py-1.5 font-medium">Code</th>
              <th className="px-3 py-1.5 font-medium">Description</th>
              <th className="px-3 py-1.5 font-medium text-right">Qty</th>
              <th className="px-3 py-1.5 font-medium">Unit</th>
              <th className="px-3 py-1.5 font-medium">Bucket</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t border-border/40" data-testid={`material-item-${i}`}>
                <td className="px-3 py-1.5 text-muted-foreground/70">{i + 1}</td>
                <td className="px-3 py-1.5 font-mono-num">{it.code || "—"}</td>
                <td className="px-3 py-1.5">{it.desc || it.description || "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono-num">{it.qty ?? it.quantity ?? "—"}</td>
                <td className="px-3 py-1.5">{it.unit || "—"}</td>
                <td className="px-3 py-1.5">{it.bucket ? <Pill tone="muted">{it.bucket}</Pill> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrackerImportDialog({ info, onClose, onCommitted }) {
  const [parsed, setParsed] = useState(null);
  const [target, setTarget] = useState(info.projectId);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr(""); setParsed(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dept", info.dept);
      const { data } = await api.post("/tracker/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/tracker/commit", { project_id: target, dept: info.dept, items: parsed.items });
      await onCommitted();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); setBusy(false); }
  };

  const deptLabel = info.dept === "design" ? "Shop Drawings" : "Material Submittal";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="w-full max-w-xl card-surface p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tracker-import-dialog">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-heading text-xl">Import {deptLabel} Log</h3>
            <div className="text-sm text-muted-foreground">{info.projectName}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X size={18} /></button>
        </div>

        {!parsed ? (
          <label className="block border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-gold/40 transition-colors" data-testid="tracker-file-drop">
            <FileSpreadsheet size={28} className="mx-auto text-gold/70 mb-2" />
            <div className="text-sm">{busy ? "Reading workbook…" : "Choose the .xlsx log file"}</div>
            <div className="text-xs text-muted-foreground mt-1">Dates & statuses are ignored — only item descriptions & scope groups are imported.</div>
            <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onFile} data-testid="tracker-file-input" disabled={busy} />
          </label>
        ) : (
          <div className="space-y-4" data-testid="tracker-confirm">
            <div className="rounded-md bg-muted/40 border border-border p-3 text-sm">
              Detected project: <b>{parsed.detected_project_name || "—"}</b> · <b>{parsed.item_count}</b> items across <b>{parsed.scope_groups.length}</b> scope groups ({parsed.scope_groups.join(", ")}).
            </div>
            <label className="block">
              <span className="text-xs text-muted-foreground mb-1 block">Link to project (auto-matched by name confidence)</span>
              <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="tracker-project-select"
                className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50">
                <option value={info.projectId}>{info.projectName} (this project)</option>
                {parsed.candidates.filter((c) => c.project_id !== info.projectId).map((c) => (
                  <option key={c.project_id} value={c.project_id}>{c.name} — {c.confidence}% match</option>
                ))}
              </select>
            </label>
            <div className="text-[11px] text-muted-foreground">Committing replaces any existing {deptLabel.toLowerCase()} items for the selected project and drives that stage's completion.</div>
          </div>
        )}

        {err && <div className="text-sm text-critical bg-critical/10 border border-critical/30 rounded-md px-3 py-2 mt-3">{err}</div>}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-border hover:bg-muted">Cancel</button>
          {parsed && (
            <button onClick={commit} disabled={busy} data-testid="tracker-commit-btn"
              className="px-4 py-2 rounded-md text-sm bg-gold text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-60">
              {busy ? "Importing…" : `Import ${parsed.item_count} items`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectForm({ form, onClose, onSaved }) {
  const isEdit = form.mode === "edit";
  const p = form.project || {};
  const [f, setF] = useState({
    job_code: p.job_code || "", name: p.name || "", client_name: p.client_name || "",
    client_importance: p.client_importance || 3, sector: p.sector || "hospitality",
    contract_value_aed: p.contract_value_aed || 0, completion_date: p.completion_date || "",
    award_date: p.award_date || "",
    ld_applicable: p.ld_applicable || false, ld_cap_pct: p.ld_cap_pct ?? "",
    retention_pct: p.retention_pct ?? "", advance_pct: p.advance_pct ?? "", status: p.status || "active",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true); setErr("");
    const payload = {
      ...f,
      client_importance: Number(f.client_importance),
      contract_value_aed: Number(f.contract_value_aed),
      ld_cap_pct: f.ld_cap_pct === "" ? null : Number(f.ld_cap_pct),
      retention_pct: f.retention_pct === "" ? null : Number(f.retention_pct),
      advance_pct: f.advance_pct === "" ? null : Number(f.advance_pct),
    };
    try {
      if (isEdit) await api.put(`/projects/${p.id}`, payload);
      else await api.post("/projects", payload);
      onSaved(); onClose();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()} data-testid="project-form"
        className="w-full max-w-2xl card-surface p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-2xl">{isEdit ? "Edit project" : "New project"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted"><X size={18} /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Job code" value={f.job_code} onChange={(v) => set("job_code", v)} testid="f-jobcode" placeholder="PII/26/001" />
          <Input label="Project name" value={f.name} onChange={(v) => set("name", v)} testid="f-name" />
          <Input label="Client name" value={f.client_name} onChange={(v) => set("client_name", v)} testid="f-client" />
          <Select label="Sector" value={f.sector} onChange={(v) => set("sector", v)} options={SECTORS} testid="f-sector" />
          <Input label="Contract value (AED)" type="number" value={f.contract_value_aed} onChange={(v) => set("contract_value_aed", v)} testid="f-value" />
          <Input label="Completion date" type="date" value={f.completion_date} onChange={(v) => set("completion_date", v)} testid="f-completion" />
          <Input label="Award date (LOA)" type="date" value={f.award_date} onChange={(v) => set("award_date", v)} testid="f-award" />
          <div>
            <span className="text-xs text-muted-foreground mb-1.5 block">Client importance (1–5)</span>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => set("client_importance", n)} data-testid={`f-importance-${n}`}
                  className={`flex-1 py-2 rounded-md border text-sm ${Number(f.client_importance) >= n ? "border-gold/50 bg-gold/15 text-gold" : "border-border text-muted-foreground"}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="f-ld-toggle">
              <input type="checkbox" checked={f.ld_applicable} onChange={(e) => set("ld_applicable", e.target.checked)} className="accent-[hsl(var(--gold))] w-4 h-4" />
              <span className="text-sm">Liquidated Damages (LD) clause</span>
            </label>
          </div>
          <Input label="LD cap %" type="number" value={f.ld_cap_pct} onChange={(v) => set("ld_cap_pct", v)} testid="f-ldcap" placeholder="10" />
          <Input label="Retention %" type="number" value={f.retention_pct} onChange={(v) => set("retention_pct", v)} testid="f-retention" placeholder="5" />
        </div>

        {err && <div className="mt-4 text-sm text-critical bg-critical/10 border border-critical/30 rounded-md px-3 py-2">{err}</div>}

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-border hover:bg-muted">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="project-save-btn"
            className="px-5 py-2 rounded-md text-sm bg-gold text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-60">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create & generate pipeline"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground mb-1.5 block">{label}</span>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50" />
    </label>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground mb-1.5 block">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50 capitalize">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
